import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { TrendingUp, Repeat, LineChart, FileText, Download, BadgeCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { getHoldingDetail, getHoldingDocuments, downloadDocumentBlob, certificateUrl } from '../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 520;
  const h = 140;
  const step = w / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * step;
    const y = h - ((p.value - min) / range) * (h - 20) - 10;
    return [x, y];
  });
  const line = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-36 w-full">
      <defs>
        <linearGradient id="hg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8BC53F" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#8BC53F" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#hg)" />
      <path d={line} fill="none" stroke="#F4BE1B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={points[i].date || `pt-${points[i].i ?? i}`} cx={c[0]} cy={c[1]} r="3" fill="#F4BE1B" />
      ))}
    </svg>
  );
}

export default function HoldingDetail({ holdingId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && holdingId) {
      setLoading(true);
      getHoldingDetail(holdingId)
        .then(setData)
        .catch(() => setData(null))
        .finally(() => setLoading(false));
      getHoldingDocuments(holdingId).then(setDocs).catch(() => setDocs([]));
    } else {
      setData(null);
      setDocs([]);
    }
  }, [open, holdingId]);

  const openDoc = async (d) => {
    try {
      const blob = await downloadDocumentBlob(d.id);
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Could not open document');
    }
  };

  const h = data?.holding;
  const gain = h ? Number(h.currentValue) - Number(h.initialValue) : 0;
  const gainPct = h && Number(h.initialValue) ? ((gain / Number(h.initialValue)) * 100).toFixed(1) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-white/10 bg-farmway-green-dark p-0 text-farmway-cream">
        {h && (
          <div className="relative h-40">
            {h.image && <img src={h.image} alt="" className="h-full w-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-farmway-green-dark via-farmway-green-dark/40 to-transparent" />
            <div className="absolute bottom-4 left-6 right-6">
              <span className="eyebrow">Your holding</span>
              <h3 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">{h.projectName}</h3>
            </div>
          </div>
        )}
        <div className="p-6">
          <DialogHeader className="sr-only">
            <DialogTitle>Holding detail</DialogTitle>
            <DialogDescription>Holding value history, trades and documents</DialogDescription>
          </DialogHeader>
          {loading ? (
            <p className="text-farmway-cream/50">Loading…</p>
          ) : !h ? (
            <p className="text-farmway-cream/50">Holding not found.</p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-white/5 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Invested</p>
                  <p className="font-display text-xl font-black text-farmway-cream">{money(h.initialValue)}</p>
                </div>
                <div className="rounded-xl bg-white/5 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Current</p>
                  <p className="font-display text-xl font-black text-farmway-cream">{money(h.currentValue)}</p>
                </div>
                <div className="rounded-xl bg-farmway-gold/10 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Gain</p>
                  <p className="flex items-center gap-1 font-display text-xl font-black text-farmway-gold">
                    <TrendingUp className="h-4 w-4" />{gainPct >= 0 ? '+' : ''}{gainPct}%
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <p className="mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">
                  <LineChart className="h-4 w-4 text-farmway-gold" /> Value history
                </p>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <Sparkline points={data.chart} />
                  <p className="mt-1 text-center text-[11px] text-farmway-cream/40">{h.units} units · {h.projectName}</p>
                </div>
              </div>

              <div className="mt-6">
                <p className="mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">
                  <Repeat className="h-4 w-4 text-farmway-gold" /> Marketplace trades
                </p>
                {data.trades?.length > 0 ? (
                  <div className="space-y-2">
                    {data.trades.map((t) => (
                      <div key={`${t.date}-${t.buyerEmail}-${t.amount}`} className="flex items-center justify-between rounded-lg bg-white/5 px-4 py-2 text-sm">
                        <span className="text-farmway-cream/70">{t.sellerEmail} → {t.buyerEmail}</span>
                        <span className="font-display font-bold text-farmway-gold">{money(t.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-farmway-cream/50">No secondary-market trades for this holding yet.</p>
                )}
              </div>

              <div className="mt-6">
                <p className="mb-2 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-farmway-cream/70">
                  <FileText className="h-4 w-4 text-farmway-gold" /> Documents
                </p>
                <div className="space-y-2">
                  <a data-testid="cert-download-btn" href={certificateUrl(holdingId)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-lg bg-farmway-gold/10 px-4 py-2.5 text-sm transition-colors hover:bg-farmway-gold/20">
                    <span className="flex items-center gap-2 text-farmway-cream"><BadgeCheck className="h-4 w-4 text-farmway-gold" /> Certificate of ownership</span>
                    <Download className="h-4 w-4 text-farmway-gold" />
                  </a>
                  {docs.map((d) => (
                    <button key={d.id} data-testid={`doc-open-${d.id}`} onClick={() => openDoc(d)} className="flex w-full items-center justify-between rounded-lg bg-white/5 px-4 py-2.5 text-sm transition-colors hover:bg-white/10">
                      <span className="flex items-center gap-2 text-left text-farmway-cream/85"><FileText className="h-4 w-4 shrink-0 text-farmway-leaf-bright" /> {d.title}</span>
                      <Download className="h-4 w-4 shrink-0 text-farmway-cream/50" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
